import { Grammars } from 'ebnf'
import assert from '@/sui/tools/assert'
import rtti from '@/sui/tools/rtti'
import ice from '@/sui/tools/ice'

const grammar = `
{ ws=explicit }
doc 
  ::= (WS* expr ([#x20#x09]* EOL | WS* comment))* EOF {pin=1, ws=implicit}

expr 
  ::= (orexpr | noorexpr) {pin=1, ws=implicit, fragment=true}
noorexpr
  ::= (andexpr | unary | par | single) {pin=1, ws=implicit, fragment=true}

unary         
  ::= WS* unop (WS+|&'(') nobinary {pin=2}
orexpr
  ::= WS* noorexpr WS+ orop WS+ expr {pin=1}
andexpr
  ::= WS* nobinary WS+ andop WS+ noorexpr {pin=1}
par   
  ::= '(' WS* expr WS* ')' {ws=implicit, pin=1, fragment=true}

nobinary 
  ::= (unary | par | single) {pin=1, ws=implicit, fragment=true}

andop
  ::= 'et'
orop
  ::= 'ou'
unop
  ::= 'non'

val
  ::= regex | string {ws=implicit, fragment=true}

single
  ::= par | val | var {ws=implicit, fragment=true}

var
  ::= varname WS* comparator WS* (regex | string)

varname
  ::= ('$'[A-Za-zéèç][A-Z-a-z0-9_éèç°]*)

comparator
  ::= ':' | '>=' | '<=' | '<' | '>' | '=' | '~'

regex
  ::= [_\\-\\@\\^\\|.A-Za-zÀ-ÖØ-öø-ÿ0-9]+ [_\\-\\@\\$\\^\\|.A-Za-zÀ-ÖØ-öø-ÿ0-9]*

string
  ::= '"' (!'"' [#x20-#xFFFF])* '"'

comment
  ::= '#' (![#x0A#x0D] [#x00-#xFFFF])* EOL

WS
  ::= comment | [#x20#x09#x0A#x0D]+ {fragment=true}
EOL
  ::= [#x0A#x0D]+|EOF
/* EOF */

`

const parser = new Grammars.Custom.Parser(grammar)

class Node {
  constructor (t) {
    this.type = t
    this.children = []
  }

  test (s) {
    return this[this.type](s)
  }

  doc (s) {
    assert.equals(this.children.length, 1)
    return this.children[0].test(s)
  }

  non (s) {
    assert.equals(this.children.length, 1)
    return !this.children[0].test(s)
  }

  et (s) {
    assert.equals(this.children.length, 2)
    return this.children[0].test(s) && this.children[1].test(s)
  }

  ou (s) {
    assert.equals(this.children.length, 2)
    return this.children[0].test(s) || this.children[1].test(s)
  }
}

class SuperiorLeaf {
  constructor (v) {
    this.value = parseInt(v)
  }

  test (s) {
    const numeric = parseInt(s)
    return this.value != null && numeric != null && numeric > this.value
  }
}

class InferiorLeaf {
  constructor (v) {
    this.value = parseInt(v)
  }

  test (s) {
    const numeric = parseInt(s)
    return this.value != null && numeric != null && numeric < this.value
  }
}

class SuperiorEqualLeaf {
  constructor (v) {
    this.value = parseInt(v)
  }

  test (s) {
    const numeric = parseInt(s)
    return this.value != null && numeric != null && numeric >= this.value
  }
}

class InferiorEqualLeaf {
  constructor (v) {
    this.value = parseInt(v)
  }

  test (s) {
    const numeric = parseInt(s)
    return this.value != null && numeric != null && numeric <= this.value
  }
}

class EqualLeaf {
  constructor (v) {
    this.value = parseInt(v)
  }

  test (s) {
    const numeric = parseInt(s)
    return this.value != null && numeric != null && this.value === numeric
  }
}

class FieldLeaf {
  constructor (f, v, o) {
    this.field = f
    if (v.length > 0 && v[0] === '"') {
      v = v.substring(1, v.length - 1)
    }

    switch (o) {
      case ':':
        this.leaf = new RegExp(`^${v}$`, 'i')
        break

      case '~':
        this.leaf = new RegExp(v, 'i')
        break

      case '=':
        this.leaf = new EqualLeaf(v)
        break

      case '>':
        this.leaf = new SuperiorLeaf(v)
        break

      case '<':
        this.leaf = new InferiorLeaf(v)
        break

      case '<=':
        this.leaf = new InferiorEqualLeaf(v)
        break
      case '>=':
        this.leaf = new SuperiorEqualLeaf(v)
        break
    }
  }

  test (s) {
    if (rtti.isString(this.field)) {
      return this.leaf.test(s[this.field])
    }
    const values = this.field(s)
    if (values.length === 0) {
      return false
    }

    for (let i in values) {
      if (this.leaf.test(values[i])) {
        return true
      }
    }
    return false
  }
}

function build (dic, f) {
  const e = parser.getAST(f)
  if (e == null) {
    return { error: 'expression incomplète' }
  }

  assert.equals(e.type, 'doc')
  const target = new Node('doc')
  let stack = [{ target: target, list: e.children }]
  const excluded = {}
  while (stack.length > 0) {
    const s = stack[0]
    for (let i in s.list) {
      const l = s.list[i]
      try {
        switch (l.type) {
          case 'andexpr':
          case 'orexpr':
            const next = new Node(l.children[1].text)
            s.target.children.push(next)
            stack.push(
              {
                target: next,
                list: [l.children[0], l.children[2]]
              }
            )
            break

          case 'unary':
            const nextu = new Node(l.children[0].text)
            s.target.children.push(nextu)
            stack.push(
              {
                target: nextu,
                list: [l.children[1]]
              }
            )
            break

          case 'var':
            const key = l.children[0].text.trim()
            const data = dic[key]
            if (data == null || data.test == null) {
              return { error: `variable ${key} inconnue` }
            }

            if (data.exclusive != null) {
              const name = key.slice(1)
              for (let e in excluded) {
                if (excluded[e].indexOf(name) !== -1) {
                  return {
                    error: e === key
                      ? `la variable ${key} ne peut être présente qu'une seule fois`
                      : `les variables ${key} et ${e} sont mutuellement exclusives`
                  }
                }
              }

              excluded[key] = data.exclusive
            }

            s.target.children.push(
              new FieldLeaf(
                data.test,
                l.children[2].text.trim(),
                l.children[1].text.trim()
              )
            )
            break

          case 'string':
            const text = l.text.substring(1, l.text.length - 1)
            s.target.children.push(new FieldLeaf('filterlabel', text, '~'))
            break

          case 'regex':
            s.target.children.push(new FieldLeaf('filterlabel', l.text, '~'))
            break

          case 'SyntaxError':
            return { error: 'erreur de syntaxe' }
        }
      } catch (e) {
        return { error: 'expression incorrecte' }
      }
    }
    stack.shift()
  }
  return (s) => { return target.test(s) }
}

function listToDictionnary (list) {
  assert.isArray(list)

  const result = {}
  for (let i = 0; i !== list.length; ++i) {
    const elt = list[i]
    result[`$${elt.key}`] = {
      test: elt.field,
      exclusive: elt.exclusive
    }
  }

  return ice.freeze(result)
}

export default {
  build,
  listToDictionnary
}
